import { useEffect, useState } from 'react';
import { Box, Button, Card, Group, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { participantActiveSessionApi } from '../../../../services/api/participantActiveSession';

/**
 * Commitment capture area for the participant active session screen
 * (MED-805 AC #12-#14, IV4).
 *
 * Renders below the slide card when the backend reports
 * `commitment_section_active === true` — per Decision #2 this is true when
 * the active section's title matches `/commitment/i`. Hides when false so
 * participants don't see a commitment prompt outside the leader-activated
 * window.
 *
 * Single textarea + Save button — consistent with the pre-session reflection
 * field pattern (E3.1). On save the commitment is upserted to
 * `session_participant_commitments` with `authored_by_user_id = participant`
 * so the post-session view (E3.4) sees it (IV4).
 *
 * MED-1139 — the Save button reads "Save Commitment" (matching the post-session
 * commitment step) and the save status uses the shared Saving… / green-dot
 * "Saved" pattern (replacing the prior "Saved {time}" treatment), adapted for
 * this dark in-session surface.
 */

const PROMPT_LABEL = 'What commitment are you taking from this session?';
// MED-1216 / AC8 — coach-attribution prompt shown when the coach entered the
// commitment and the participant hasn't saved their own yet.
const COACH_ATTRIBUTION_LABEL =
  'Noted by your coach, please review and update if needed';
const STATUS_TEXT = 'rgba(255, 255, 255, 0.7)';

export interface CommitmentAreaProps {
  sessionId: string;
  /** AC #12 trigger from the backend. When false, this component renders nothing. */
  active: boolean;
  /**
   * MED-1030 — DEV-only static preview (`/dev/participant-session`). The fixture
   * supplies a mock `sessionId`, so skip the hydrate fetch + save round-trip
   * (they'd 401 and bounce the page to /login) and just exercise the UI.
   */
  preview?: boolean;
}

export function CommitmentArea({
  sessionId,
  active,
  preview = false,
}: CommitmentAreaProps) {
  const [commitmentText, setCommitmentText] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // MED-1216 / AC8 — a coach-entered value the participant hasn't confirmed yet.
  const [coachAttributed, setCoachAttributed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the prior saved commitment (if any) the first time the area
  // becomes active. A participant returning to the screen mid-section should
  // see their previously-saved text in the field.
  useEffect(() => {
    if (!active || hydrated || preview) return;
    let cancelled = false;
    void (async () => {
      try {
        const result =
          await participantActiveSessionApi.getCommitment(sessionId);
        if (cancelled) return;
        setCommitmentText(result.commitment_text ?? '');
        setCoachAttributed(result.authored_by_coach);
        // Show the "Saved" status only for the participant's OWN persisted
        // commitment — a coach-entered value still needs the participant's review.
        setJustSaved(Boolean(result.updated_at) && !result.authored_by_coach);
      } catch {
        // Non-blocking — the participant can still type and save.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, hydrated, preview, sessionId]);

  const handleChange = (value: string) => {
    setCommitmentText(value);
    // Typing makes the field dirty again — clear the "Saved" indicator.
    setJustSaved(false);
  };

  const handleSave = async () => {
    setError(null);
    if (preview) {
      // DEV harness — no backend; simulate a successful explicit save so the
      // Saving… / Saved status can be exercised without a network round-trip.
      setJustSaved(true);
      setCoachAttributed(false);
      return;
    }
    setSaving(true);
    try {
      const result = await participantActiveSessionApi.putCommitment(
        sessionId,
        commitmentText
      );
      setCommitmentText(result.commitment_text ?? '');
      setJustSaved(true);
      // AC5 — a self-save transfers ownership; drop the coach attribution.
      setCoachAttributed(false);
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasCommitment = commitmentText.trim().length > 0;

  if (!active) return null;

  return (
    <Card
      data-testid="participant-active-commitment-area"
      radius="md"
      padding="md"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.10)',
        marginTop: 24,
      }}
    >
      <Stack gap="sm">
        <Text
          fw={600}
          c={coachAttributed ? 'yellow.4' : 'white'}
          data-testid="participant-active-commitment-prompt"
        >
          {coachAttributed ? COACH_ATTRIBUTION_LABEL : PROMPT_LABEL}
        </Text>
        <Textarea
          value={commitmentText}
          onChange={e => handleChange(e.currentTarget.value)}
          placeholder="Write what you'll commit to."
          minRows={3}
          autosize
          maxRows={8}
          data-testid="participant-active-commitment-textarea"
          styles={{
            input: {
              backgroundColor: '#FFFFFF',
              color: '#1A1F36',
            },
          }}
        />
        <Stack gap={4}>
          {/* AC1 — Save disabled until non-empty; tooltip explains why. The Box
              wrapper lets the tooltip show while the button is disabled. */}
          <Tooltip
            label="Enter a commitment before saving."
            disabled={hasCommitment || saving}
            withArrow
            position="top"
          >
            <Box style={{ alignSelf: 'flex-end', display: 'inline-block' }}>
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={!hasCommitment || saving}
                data-testid="participant-active-commitment-save"
              >
                Save Commitment
              </Button>
            </Box>
          </Tooltip>
          {error ? (
            <Text
              size="xs"
              c="dangerRed.4"
              ta="right"
              data-testid="participant-active-commitment-error"
            >
              {error}
            </Text>
          ) : (
            <Group justify="flex-end" gap={6} mih={18}>
              <CommitmentSaveStatus saving={saving} saved={justSaved} />
            </Group>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * MED-1139 — Saving… / green-dot "Saved" status, mirroring the post-session
 * commitment + session-notes pattern, tuned for the dark in-session surface.
 */
function CommitmentSaveStatus({
  saving,
  saved,
}: {
  saving: boolean;
  saved: boolean;
}) {
  if (saving) {
    return (
      <Text size="xs" c={STATUS_TEXT}>
        Saving…
      </Text>
    );
  }
  if (saved) {
    return (
      <Group gap={6} align="center" wrap="nowrap">
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-medleyGreen-6)',
          }}
        />
        <Text size="xs" c={STATUS_TEXT}>
          Saved
        </Text>
      </Group>
    );
  }
  return null;
}
